// Регистр: /api/v1/ai/** — snake_case. Этот файл и studioStartTypes.ts — единственные,
// где он появляется; camelCase-контракты (/ai-credits/**, /gamified-courses/**) живут отдельно.
//
// Джоба и её результаты собраны здесь; смета и тела запуска — в studioStartTypes.ts
// (файл-источник превышал лимит длины eslint, разрезан по смыслу, а не по объёму).
import type { Lang } from '@/types/lang'

export type StudioJobType =
  'course_generation' | 'course_image_generation' | 'course_block_edit' | 'course_translation'

export type StudioJobStatus =
  | 'pending'
  | 'running'
  | 'success'
  | 'failed'
  | 'retrying'
  /** Прогон встал на вопросе автору и ждёт ответа — до суток. НЕ терминальный:
   *  опрос продолжается, курс остаётся занятым. */
  | 'awaiting_author'
  /** Автор отменил прогон (`POST ai/studio/jobs/{id}/cancel`). Терминальный и
   *  НЕ ошибка: сделанное до отмены осталось в курсе, списано по факту. */
  | 'cancelled'

export interface JobEvent {
  stage: string
  message: string
  at: string
}

export type GateState = 'pending' | 'answered' | 'expired'

export interface GateOption {
  key: string
  label: string | null
  /** Непустой — вариант это картинка-кандидат («пилот»), и рисовать его надо
   *  плиткой. Единственный признак, по которому меняется отрисовка вопроса:
   *  по `kind` ветвиться нельзя, он свободная строка. */
  image_url: string | null
}

export interface Gate {
  id: number
  /** Свободная строка (`style_pilot`, `tone`, …) — показывать можно, ветвить логику нельзя. */
  kind: string
  question: string
  options: GateOption[]
  allows_text: boolean
  /** Считает СЕРВЕР. По `expires_at` и часам браузера состояние не выводить. */
  state: GateState
  answer: { option_key: string | null; text: string | null } | null
  /** Метки БЕЗ зоны — разбирать только через `lib/serverTime`. */
  asked_at: string
  answered_at: string | null
  expires_at: string
}

interface JobResultBase {
  events?: JobEvent[]
  skipped?: Record<string, string>
  /** Машинный код причины пропуска (PRD #60), ключи те же, что у `skipped`. */
  skipped_codes?: Record<string, string>
  summary?: string
  credits_charged?: string
  tokens_in?: number
  tokens_out?: number
  /** id продолжения — появляется у РОДИТЕЛЯ, когда его продолжили. */
  resumed_by?: number
}

/** Сколько единиц перевод реально записал, по видам (§6.6 контракта). */
export interface TranslatedCounts {
  course?: number
  lesson?: number
  block?: number
}

export interface GenerationResult extends JobResultBase {
  lessons_done?: number
  lessons_total?: number
  lessons?: Record<string, number>
}

export interface ImageResult extends JobResultBase {
  images_done?: number
  images_total?: number
  images?: Record<string, string>
  /** Места, где на исходной картинке не нашлось текста: картинка скопирована, а
   *  не перерисована. По прогрессу это готовое место (§6.6). */
  images_reused?: Record<string, boolean>
}

export interface BlockEditResult extends JobResultBase {
  blocks_done?: number
  blocks_total?: number
  blocks?: Record<string, boolean>
}

export interface TranslationResult extends JobResultBase {
  translations_done?: number
  translations_total?: number
  translations?: Record<string, boolean>
  /** Цепочка: после перевода бэк сам ставит джобу на картинки (§7.4). */
  next_job_id?: number
  /** Итог перевода по видам (§6.6) — сумма расходится с `translations_done`,
   *  когда единица перевода не совпадает с единицей учёта прогресса. */
  translated?: TranslatedCounts
}

interface JobBase {
  id: number
  target_type: 'gamified_course'
  target_id: number
  /** У картинок и правки блоков приходит "-" — это не язык, не показывать. */
  target_lang: Lang | '-'
  status: StudioJobStatus
  error_code: string | null
  error_message: string | null
  created_at: string
  finished_at: string | null
  /** Вопросы прогона, включая уже отвеченные: после перезагрузки страницы
   *  диалог обязан быть целым, а хранилища чата на бэке нет. На сервере поле
   *  есть всегда (у обычной джобы — пустой список); опциональным оставлено
   *  ради тестовых фикстур, которые его не несут. */
  gates?: Gate[]
  /** Подсказка «можно ли продолжить». Необязательное: старый бэк поля не несёт,
   *  и до выката прода его не будет. Отсутствие = кнопку не рисуем. */
  resume?: ResumeHint | null
}

/**
 * Подсказка сервера «какую кнопку рисовать». Считает её СЕРВЕР теми же
 * правилами, по которым ответит сама ручка возобновления, — вычислять в
 * браузере нельзя, разойдёмся с api.
 *
 * `null` у всего, что продолжать нечем: статейные и MCP-джобы, а также
 * студийные прогоны, которые ещё идут.
 */
export interface ResumeHint {
  available: boolean
  /** Код причины, когда `available: false`. Тексты — в `lib/resumeFailureText`. */
  reason: string | null
  /** Сколько единиц доделает продолжение. Показывать можно («доделать 2 шага»). */
  plan_size: number | null
  /** Заполнен только при `reason: "already_resumed"` — id продолжения. */
  job_id: number | null
}

// Размечено по `type`: у четырёх операций разный result, и потребитель
// (C-G) обязан сужать его без приведений.
export type StudioJob =
  | (JobBase & { type: 'course_generation'; result: GenerationResult | null })
  | (JobBase & { type: 'course_image_generation'; result: ImageResult | null })
  | (JobBase & { type: 'course_block_edit'; result: BlockEditResult | null })
  | (JobBase & { type: 'course_translation'; result: TranslationResult | null })

/** Терминальные статусы — единственное определение на весь модуль: его читают
 *  и поллер (когда прекращать опрос), и useCourseJob (что показывать). */
export const isTerminalStatus = (status?: string): boolean =>
  status === 'success' || status === 'failed' || status === 'cancelled'

/** Открытый вопрос прогона — единственное определение на весь модуль.
 *  Читают и лента (нарисовать карточку активной), и панель (не показывать
 *  индикатор прогона там, где ждут автора). */
export const pendingGateOf = (job: { gates?: Gate[] } | null | undefined): Gate | null =>
  (job?.gates ?? []).find((g) => g.state === 'pending') ?? null

/**
 * Прогоны, которые пишут в блоки ПО ХОДУ, а не только в терминале (§6.2) —
 * правка, картинки и перевод. Единственный список на весь модуль: `JobPoller`
 * (инвалидация блоков на каждом тике) и гейт реордера
 * (`useIsBlockWritingRunActive`) читают отсюда — второй копии не заводить.
 */
export const BLOCK_WRITING_JOB_TYPES: ReadonlySet<StudioJobType> = new Set([
  'course_block_edit',
  'course_image_generation',
  // Перевод пишет в те же блоки (`*En`) и НЕ несёт списка блоков: воркфлоу
  // взял снимок всего курса на старте (§7.5).
  'course_translation',
])
